import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

// HaulLogix Logo Component
const HaulLogixLogo = () => (
  <svg width="48" height="42" viewBox="0 0 32 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="forgotLogoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#4ADE80" />
        <stop offset="50%" stopColor="#D4A574" />
        <stop offset="100%" stopColor="#16A34A" />
      </linearGradient>
    </defs>
    <path 
      d="M0 0L10 14L0 28H6L12.5 18.5L16 24L19.5 18.5L26 28H32L22 14L32 0H26L16 14L6 0H0Z" 
      fill="url(#forgotLogoGradient)"
    />
    <path d="M16 8L20 14H12L16 8Z" fill="#0a0c10"/>
    <rect x="13" y="12" width="6" height="1.5" fill="#0a0c10"/>
  </svg>
);

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setSubmitted(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
      toast.success('If an account exists, reset instructions have been sent.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3">
              <HaulLogixLogo />
              <span className="text-2xl font-bold tracking-wider uppercase">
                <span className="text-white">HAUL</span><span className="text-[#4ADE80]">LOGIX</span>
              </span>
            </Link>
          </div>

          <Card className="glass-card border-border">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-[#4ADE80]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#4ADE80]" />
              </div>
              <CardTitle className="text-xl mb-4">Check Your Email</CardTitle>
              <CardDescription className="text-muted-foreground mb-6">
                If an account exists for <span className="text-[#4ADE80]">{email}</span>, 
                we've sent password reset instructions to your inbox.
              </CardDescription>
              <p className="text-sm text-muted-foreground mb-6">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <div className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setSubmitted(false)}
                >
                  Try Another Email
                </Button>
                <Link to="/login" className="block">
                  <Button className="w-full glow-gold">
                    Back to Login
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <HaulLogixLogo />
            <span className="text-2xl font-bold tracking-wider uppercase">
              <span className="text-white">HAUL</span><span className="text-[#4ADE80]">LOGIX</span>
            </span>
          </Link>
        </div>

        <Card className="glass-card border-border">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold">Forgot Password?</CardTitle>
            <CardDescription className="text-muted-foreground">
              Enter your email and we'll send you reset instructions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-secondary border-border"
                    data-testid="forgot-password-email"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full glow-gold"
                disabled={loading}
                data-testid="forgot-password-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#4ADE80] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
